import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useJourney } from "@/context/JourneyContext";
import { StageFrame, Eyebrow, EASE } from "@/components/StageFrame";
import { ConsultantNav } from "@/components/Navigation";
import { Disclaimer } from "@/components/Disclaimer";
import { RewardPopup } from "@/components/RewardPopup";
import { api, money, weekly } from "@/lib/api";
import { BedDouble, Bath, Car, Layers, Loader2, Sparkles, Maximize2, X } from "lucide-react";

const KITCHEN_REWARD = { name: "Kitchen Upgrade", value: 900 };

const LABELS = {
  best_life: { tag: "Best Fit", color: "#E2674B" },
  best_financial: { tag: "Smart Choice", color: "#5B8C7B" },
  aspirational: { tag: "More Lifestyle", color: "#B6A78C" },
};

function HomeCard({ home, kind, big, delay, selected, onSelect }) {
  const [showFp, setShowFp] = useState(false);
  if (!home) return null;
  const meta = LABELS[kind];
  return (
    <motion.div
      data-testid={`reveal-card-${kind}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: EASE, delay }}
      className={`group relative flex flex-col overflow-hidden rounded-3xl border shadow-2xl transition-transform hover:-translate-y-1 bg-surface ${selected ? "border-brand ring-2 ring-brand" : "border-line"}`}
    >
      {/* Floor plan */}
      <div className={`relative flex items-center justify-center bg-white ${big ? "h-[300px] lg:h-[440px]" : "h-56"}`}>
        <img
          src={home.floorplan_image_url || home.image_url}
          alt={`${home.name} floor plan`}
          className="h-full w-full object-contain p-2"
        />
        <span className="absolute top-4 left-4 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-white shadow" style={{ background: meta.color }}>{meta.tag}</span>
        {selected && <span className="absolute bottom-3 right-3 rounded-full bg-brand px-3 py-1 text-[11px] font-semibold text-white shadow">Selected</span>}
        {home.floorplan_image_url && (
          <button
            data-testid={`reveal-view-floorplan-${kind}`}
            onClick={(e) => { e.stopPropagation(); setShowFp(true); }}
            className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-black/60 px-3 py-1.5 text-[11px] font-medium text-white backdrop-blur-sm transition hover:bg-black/80"
          >
            <Maximize2 size={12} /> Expand
          </button>
        )}
      </div>

      {/* Info */}
      <div className="flex flex-1 flex-col p-6 md:p-7 text-forest">
        <h3 className={`font-serif font-light leading-none ${big ? "text-4xl md:text-5xl" : "text-2xl"}`}>{home.name}</h3>
        <div className="mt-3 flex flex-wrap items-center gap-4 text-mutedText text-sm">
          <span className="flex items-center gap-1.5"><BedDouble size={15} />{home.beds}</span>
          <span className="flex items-center gap-1.5"><Bath size={15} />{home.baths}</span>
          <span className="flex items-center gap-1.5"><Car size={15} />{home.garage}</span>
          <span className="flex items-center gap-1.5"><Layers size={15} />{home.storeys} storey</span>
          <span>{home.house_size_m2}m²</span>
        </div>

        <div className="mt-4 flex items-end gap-3">
          <div>
            <span className="block text-[11px] uppercase tracking-widest text-mutedText">Repayment</span>
            <span className={`font-serif text-brand ${big ? "text-5xl" : "text-4xl"}`} data-testid={`reveal-repayment-${kind}`}>{weekly(home.repayment_weekly)}<span className="text-lg text-forest/50">/wk</span></span>
          </div>
          <span className="mb-1.5 text-mutedText text-sm">from {money(home.base_price)} home</span>
        </div>

        {home.explanation && (
          <p className={`mt-4 text-forest/80 leading-relaxed ${big ? "text-base" : "text-sm line-clamp-3"}`}>
            <span className="inline-flex items-center gap-1.5 text-mutedText text-xs uppercase tracking-widest mb-1"><Sparkles size={12} /> Why this</span><br />
            {home.explanation}
          </p>
        )}
        {big && home.upgrades_weekly?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {home.upgrades_weekly.slice(0, 3).map((u, i) => (
              <span key={i} className="rounded-full border border-brand/50 bg-brand/15 px-3.5 py-1.5 text-xs font-semibold text-brand">{u.name} +{weekly(u.weekly)}/wk</span>
            ))}
          </div>
        )}

        <button
          data-testid={`reveal-select-${kind}`}
          onClick={() => onSelect(home, kind)}
          className={`mt-5 w-full rounded-full px-5 py-3 text-sm font-medium transition ${selected ? "bg-brand text-white" : "border border-line bg-white/10 text-forest hover:bg-white/20"}`}
        >
          {selected ? "This is our home" : "Choose this home"}
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {showFp && (
            <motion.div
              data-testid={`reveal-floorplan-lightbox-${kind}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowFp(false)}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
            >
              <button
                data-testid={`reveal-floorplan-close-${kind}`}
                onClick={() => setShowFp(false)}
                className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
              >
                <X size={20} />
              </button>
              <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center">
                <span className="mb-3 font-serif text-2xl text-white">{home.name} — floor plan</span>
                <motion.img
                  initial={{ scale: 0.92 }} animate={{ scale: 1 }} exit={{ scale: 0.92 }}
                  transition={{ ease: EASE, duration: 0.35 }}
                  src={home.floorplan_image_url}
                  alt={`${home.name} floor plan`}
                  className="max-h-[85vh] max-w-[92vw] rounded-xl bg-white object-contain shadow-2xl"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </motion.div>
  );
}

export default function Reveal() {
  const { session, data, next, back, mergeData, addReward } = useJourney();
  const [matches, setMatches] = useState(data.matches || null);
  const [loading, setLoading] = useState(!data.matches);
  const [selected, setSelected] = useState(data.selected_home?.kind || null);
  const [showReward, setShowReward] = useState(false);

  useEffect(() => {
    const bBudget = data.budget?.home_budget;
    const stale = matches && bBudget != null && matches.home_budget !== bBudget;
    if (matches && !stale) return;
    setLoading(true);
    let alive = true;
    api.match(session.id).then((r) => {
      if (!alive) return;
      setMatches(r); mergeData({ matches: r }); setLoading(false);
    }).catch(() => { toast.error("Matching failed."); setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const chooseHome = (home, kind) => {
    const firstSelect = !selected;
    setSelected(kind);
    const land = data.budget?.land_price || 0;
    const payload = {
      kind, name: home.name, base_price: home.base_price,
      purchase_price: (home.base_price || 0) + land, image_url: home.image_url,
      beds: home.beds, baths: home.baths, storeys: home.storeys,
      repayment_weekly: home.repayment_weekly,
    };
    mergeData({ selected_home: payload });
    api.patchSession(session.id, { data: { selected_home: payload } }).catch(() => {});
    // HOOK 1 — first reward once a home is chosen (Section 1 complete)
    if (firstSelect && !(data.rewards || []).some((r) => r.name === KITCHEN_REWARD.name)) {
      addReward(KITCHEN_REWARD);
      setShowReward(true);
    } else {
      toast.success(`${home.name} selected — let's see your life in it.`);
    }
  };

  const proceed = () => {
    if (!selected) { toast.error("Choose a home together to continue."); return; }
    next();
  };

  if (loading) {
    return (
      <StageFrame testId="stage-reveal" className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 size={40} className="animate-spin text-brand" />
        <p className="font-serif text-3xl text-forest mt-6">Searching every design for your life…</p>
        <p className="text-mutedText mt-2 text-sm">Matching your Life Profile against the library.</p>
      </StageFrame>
    );
  }

  return (
    <StageFrame testId="stage-reveal" className="px-5 md:px-12 pt-20 pb-32">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <Eyebrow>Three homes, matched to you</Eyebrow>
            <h1 className="font-serif text-4xl md:text-6xl font-light tracking-tight text-forest mt-2">Homes for the life you described.</h1>
          </div>
          <p className="text-mutedText text-sm max-w-xs">Chosen from your home budget of <span className="text-forest font-medium">{money(matches?.home_budget)}</span>. Pick the one that feels right.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 mt-8">
          <div className="lg:col-span-7"><HomeCard home={matches?.best_life} kind="best_life" big delay={0.1} selected={selected === "best_life"} onSelect={chooseHome} /></div>
          <div className="lg:col-span-5 flex flex-col gap-5">
            <HomeCard home={matches?.best_financial} kind="best_financial" delay={0.25} selected={selected === "best_financial"} onSelect={chooseHome} />
            <HomeCard home={matches?.aspirational} kind="aspirational" delay={0.4} selected={selected === "aspirational"} onSelect={chooseHome} />
          </div>
        </div>

        <Disclaimer short className="mt-6 justify-center" />
      </div>

      <ConsultantNav testId="stage-reveal" onBack={back} onNext={proceed} nextLabel="See your life in this home" disabled={!selected} />
      <RewardPopup
        open={showReward}
        onClose={() => setShowReward(false)}
        title="FREE Kitchen Upgrade"
        value={900}
        subtitle="Automatically attached to your home — yours to use later."
      />
    </StageFrame>
  );
}
